import { create } from 'zustand';
import type { ReactNode } from 'react';
import { SmartHintData } from '../components/ContextHelp/SmartHint';

interface HintStore {
  currentHint: SmartHintData | null;
  hintQueue: SmartHintData[];
  shownHints: Set<string>; // 一度表示したヒントのID

  // ヒントを表示
  showHint: (hint: SmartHintData, options?: { force?: boolean }) => void;

  // 現在のヒントを閉じる
  closeHint: () => void;

  // ヒントをリセット（再表示可能にする）
  resetHints: () => void;
}

export const useHintStore = create<HintStore>((set, get) => ({
  currentHint: null,
  hintQueue: [],
  shownHints: new Set(),

  showHint: (hint, options = {}) => {
    const { shownHints, currentHint, hintQueue } = get();

    // 既に表示したヒントは基本的にスキップ（forceオプションで強制表示可能）
    if (!options.force && shownHints.has(hint.id)) {
      return;
    }

    // 現在ヒントが表示中の場合はキューに追加
    if (currentHint) {
      set({ hintQueue: [...hintQueue, hint] });
      return;
    }

    // ヒントを表示
    set((state) => ({
      currentHint: hint,
      shownHints: new Set([...state.shownHints, hint.id])
    }));
  },

  closeHint: () => {
    const { hintQueue } = get();

    // キューに次のヒントがある場合は表示
    if (hintQueue.length > 0) {
      const [nextHint, ...remainingQueue] = hintQueue;
      set((state) => ({
        currentHint: nextHint,
        hintQueue: remainingQueue,
        shownHints: new Set([...state.shownHints, nextHint.id])
      }));
    } else {
      set({ currentHint: null });
    }
  },

  resetHints: () => {
    set({
      shownHints: new Set(),
      currentHint: null,
      hintQueue: []
    });
  }
}));

// 便利なヒント関数
export const showSmartHint = (
  title: string,
  content: ReactNode,
  options?: {
    id?: string;
    icon?: string;
    duration?: number;
    force?: boolean;
  }
) => {
  const hint: SmartHintData = {
    id: options?.id || `hint-${Date.now()}`,
    title,
    content,
    icon: options?.icon,
    duration: options?.duration
  };

  useHintStore.getState().showHint(hint, { force: options?.force });
};